export const darkOpsTheme = {
  colors: {
    background: "#000000",
    foreground: "#ffffff",
    card: "#111111",
    cardHover: "#1a1a1a",
    border: "#333333",
    borderActive: "#555555",
    accent: "#00f5ff",
    accentDark: "#00d4ff",
    warning: "#ffcc00",
    danger: "#ff4444",
    success: "#44ff44",
    info: "#4488ff",
    muted: "#666666",
    mutedForeground: "#999999",
    
    // Semantic colors for status indicators
    status: {
      operational: "#00ff88",
      warning: "#ffcc00", 
      critical: "#ff4444",
      maintenance: "#4488ff",
      unknown: "#999999"
    },
    
    // Security level colors
    security: {
      alpha: "#00f5ff",
      beta: "#88ff44", 
      gamma: "#ffcc00",
      delta: "#ff8844",
      omega: "#ff4444"
    },
    
    // Gradient definitions for advanced UI elements
    gradients: {
      primary: "linear-gradient(135deg, #000000 0%, #111111 100%)",
      accent: "linear-gradient(135deg, #00f5ff 0%, #0088ff 100%)",
      warning: "linear-gradient(135deg, #ffcc00 0%, #ff8800 100%)",
      danger: "linear-gradient(135deg, #ff4444 0%, #cc0000 100%)"
    }
  },
  
  fonts: {
    body: "'IBM Plex Sans', 'Segoe UI', 'Roboto', sans-serif",
    headings: "'IBM Plex Sans', 'Segoe UI', 'Roboto', sans-serif",
    mono: "'IBM Plex Mono', 'Fira Code', 'Monaco', monospace",
    sizes: {
      xs: "0.75rem",
      sm: "0.875rem", 
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem"
    },
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      black: 900
    }
  },
  
  shadows: {
    card: "0 0 10px rgba(0,255,255,0.1)",
    cardHover: "0 0 20px rgba(0,255,255,0.2)",
    glow: "0 0 30px rgba(0,255,255,0.3)",
    inner: "inset 0 2px 4px 0 rgba(0,0,0,0.5)",
    none: "none"
  },
  
  borders: {
    radius: {
      none: "0",
      sm: "0.125rem",
      default: "0.25rem", 
      md: "0.375rem",
      lg: "0.5rem",
      xl: "0.75rem",
      "2xl": "1rem",
      full: "9999px"
    },
    width: {
      0: "0",
      1: "1px",
      2: "2px", 
      4: "4px",
      8: "8px"
    }
  },
  
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem", 
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem"
  },
  
  animation: {
    duration: {
      fast: "150ms",
      normal: "300ms", 
      slow: "500ms"
    },
    easing: {
      ease: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)"
    }
  },
  
  // Component-specific theme overrides
  components: {
    card: {
      background: "#111111",
      border: "#333333",
      shadow: "0 0 10px rgba(0,255,255,0.1)"
    },
    button: {
      primary: {
        background: "#00f5ff",
        backgroundHover: "#00d4ff",
        foreground: "#000000"
      },
      secondary: {
        background: "transparent",
        backgroundHover: "#333333", 
        foreground: "#ffffff",
        border: "#666666"
      },
      danger: {
        background: "#ff4444",
        backgroundHover: "#ff6666",
        foreground: "#ffffff"
      }
    },
    input: {
      background: "#111111",
      border: "#333333",
      borderFocus: "#00f5ff",
      foreground: "#ffffff",
      placeholder: "#666666"
    },
    table: {
      header: "#1a1a1a",
      row: "#111111",
      rowHover: "#1a1a1a",
      border: "#333333"
    }
  }
};

// Utility functions for theme usage
export const getStatusColor = (status: string) => {
  switch(status.toLowerCase()) {
    case 'operational': 
    case 'active':
    case 'online':
      return darkOpsTheme.colors.status.operational;
    case 'warning':
    case 'degraded':
      return darkOpsTheme.colors.status.warning;
    case 'critical':
    case 'error':
    case 'offline':
      return darkOpsTheme.colors.status.critical;
    case 'maintenance':
      return darkOpsTheme.colors.status.maintenance;
    default:
      return darkOpsTheme.colors.status.unknown;
  }
};

export const getSecurityLevelColor = (level: string) => {
  switch(level.toLowerCase()) {
    case 'alpha':
      return darkOpsTheme.colors.security.alpha;
    case 'beta':
      return darkOpsTheme.colors.security.beta;
    case 'gamma':
      return darkOpsTheme.colors.security.gamma;
    case 'delta':
      return darkOpsTheme.colors.security.delta;
    case 'omega':
      return darkOpsTheme.colors.security.omega;
    default:
      return darkOpsTheme.colors.muted;
  }
};